import type { Ref } from 'vue'
import Vue from 'vue'

import type { WithRelation, Activity } from '@/types'
import { ActivityType, ActivityStepType } from '@/types'
import { getCurrentInstance } from '@/util'
import { useDialogify } from '@/use'

import type {
  DatabaseRelation,
  RelationsContainer,
  ActivityLinkResult,
} from '../ActivityLink.types'
import {
  ActivityLinkActivityTarget,
  ActivityLinkTarget,
} from '../ActivityLinkTarget'
import { UnableToLinkPollActivityException } from './useActivityLink.exceptions'

export default function useLinkActivity<
  T extends ActivityLinkTarget,
  R extends DatabaseRelation
>(container: Ref<RelationsContainer<T, R>>) {
  const instance = getCurrentInstance()
  const { dialogify } = useDialogify()
  const h = instance.proxy.$createElement

  async function linkDefaultActivity(activity: WithRelation<Activity>) {
    const { default: options } = await import('../ActivityLink.vue')
    const Component = Vue.extend({ extends: options })
    return new Promise<ActivityLinkResult<R>[] | null>((resolve) => {
      const { hide } = dialogify(
        [
          h(Component, {
            props: {
              target: new ActivityLinkActivityTarget(activity.relation.id),
              container: container.value,
            },
            on: {
              cancel: () => {
                hide()
                resolve(null)
              },
              success: (results: ActivityLinkResult<R>[]) => {
                hide()
                resolve(results)
              },
            },
          }),
        ],
        { title: 'Link Activity', size: 'sm' }
      )
    })
  }

  async function linkFactMatchActivity(activity: WithRelation<Activity>) {
    const { default: options } = await import('../ActivityLinkFactMatch.vue')
    const Component = Vue.extend({ extends: options })
    return new Promise<ActivityLinkResult<R>[] | null>((resolve) => {
      const { hide } = dialogify(
        [
          h(Component, {
            props: {
              target: new ActivityLinkActivityTarget(activity.relation.id),
              container: container.value,
            },
            on: {
              cancel: () => {
                hide()
                resolve(null)
              },
              success: (results: ActivityLinkResult<R>[]) => {
                hide()
                resolve(results)
              },
            },
          }),
        ],
        { title: 'Link Activity', size: 'sm' }
      )
    })
  }

  async function linkEvaluateActivity(activity: WithRelation<Activity>) {
    const { default: options } = await import('../ActivityEvaluate.vue')
    const Component = Vue.extend({ extends: options })
    return new Promise<ActivityLinkResult<R>[] | null>((resolve) => {
      const { hide } = dialogify(
        [
          h(Component, {
            props: {
              target: new ActivityLinkActivityTarget(activity.relation.id),
              container: container.value,
            },
            on: {
              cancel: () => {
                hide()
                resolve(null)
              },
              success: (results: ActivityLinkResult<R>[]) => {
                hide()
                resolve(results)
              },
            },
          }),
        ],
        { title: 'Link Evaluate', size: 'sm' }
      )
    })
  }

  async function linkPollActivity(activity: WithRelation<Activity>) {
    const playStep = activity.steps.find(
      (a) => a.type === ActivityStepType.Play
    )
    if (playStep === undefined) {
      throw new UnableToLinkPollActivityException()
    }
    const { default: options } = await import('../ActivityLinkPoll.vue')
    const Component = Vue.extend({ extends: options })
    return new Promise<ActivityLinkResult<R>[] | null>((resolve) => {
      const { hide } = dialogify(
        [
          h(Component, {
            props: {
              target: new ActivityLinkActivityTarget(activity.relation.id),
              step: playStep,
              container: container.value,
            },
            on: {
              cancel: () => {
                hide()
                resolve(null)
              },
              success: (results: ActivityLinkResult<R>[]) => {
                hide()
                resolve(results)
              },
            },
          }),
        ],
        { title: 'Link Poll Answers', size: 'sm' }
      )
    })
  }

  async function linkRatingPollActivity(activity: WithRelation<Activity>) {
    const { default: options } = await import('../ActivityLinkRatingPoll.vue')
    const Component = Vue.extend({ extends: options })
    return new Promise<ActivityLinkResult<R>[] | null>((resolve) => {
      const { hide } = dialogify(
        [
          h(Component, {
            props: {
              target: new ActivityLinkActivityTarget(activity.relation.id),
              container: container.value,
            },
            on: {
              cancel: () => {
                hide()
                resolve(null)
              },
              success: (results: ActivityLinkResult<R>[]) => {
                hide()
                resolve(results)
              },
            },
          }),
        ],
        { title: 'Link Rating Poll', size: 'sm' }
      )
    })
  }

  async function linkTwoTruthsActivity(activity: WithRelation<Activity>) {
    const { default: options } = await import('../ActivityLinkTwoTruths.vue')
    const Component = Vue.extend({ extends: options })
    return new Promise<ActivityLinkResult<R>[] | null>((resolve) => {
      const { hide } = dialogify(
        [
          h(Component, {
            props: {
              target: new ActivityLinkActivityTarget(activity.relation.id),
              container: container.value,
            },
            on: {
              cancel: () => {
                hide()
                resolve(null)
              },
              success: (results: ActivityLinkResult<R>[]) => {
                hide()
                resolve(results)
              },
            },
          }),
        ],
        { title: 'Link Two Truths', size: 'sm' }
      )
    })
  }

  async function link(activity: WithRelation<Activity>) {
    if (activity.type === ActivityType.Poll) {
      return linkPollActivity(activity)
    }
    if (activity.type === ActivityType.RatingPoll) {
      return linkRatingPollActivity(activity)
    }
    if (activity.type === ActivityType.FactMatch) {
      return linkFactMatchActivity(activity)
    }
    if (activity.type === ActivityType.Evaluate) {
      return linkEvaluateActivity(activity)
    }
    if (activity.type === ActivityType.TwoTruthsReveal) {
      return linkTwoTruthsActivity(activity)
    }
    return linkDefaultActivity(activity)
  }

  return {
    link,
  }
}
